// src/components/SubscriptionGate.jsx
// Renders a full-screen lock when subscription is expired.
// Wrap around <Layout /> in App.jsx ProtectedRoute, similar to OnboardingModal.

import { useNavigate } from 'react-router-dom';
import { Lock, RefreshCw, Zap, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';

export default function SubscriptionGate({ children }) {
  const { isLocked, isInGrace, loading, accessStatus, subscription, graceDaysRemaining } = useSubscription();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // While loading subscription, show nothing (prevents flash)
  if (loading) return children;

  // Grace period — access allowed, banner handles the warning
  // Expired/suspended — show lock screen
  if (!isLocked) return children;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg-primary)' }}>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 opacity-5 rounded-full blur-3xl"
          style={{ backgroundColor: 'var(--accent-danger)' }} />
        <div className="absolute bottom-0 -right-4 w-96 h-96 opacity-5 rounded-full blur-3xl"
          style={{ backgroundColor: 'var(--accent-primary)' }} />
      </div>

      <div className="relative z-10 max-w-md w-full text-center space-y-6">

        {/* Icon */}
        <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center"
          style={{ backgroundColor: 'var(--accent-danger)15', border: '2px solid var(--accent-danger)30' }}>
          <Lock className="w-10 h-10" style={{ color: 'var(--accent-danger)' }} />
        </div>

        {/* Message */}
        <div>
          <h1 className="text-2xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
            {accessStatus === 'suspended' ? 'Account Suspended' : 'Subscription Expired'}
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {accessStatus === 'suspended'
              ? 'Your account has been suspended. Please contact support to restore access.'
              : `Your ${subscription?.plan_name || 'platform'} subscription has expired and the grace period has ended. Renew to restore full access.`
            }
          </p>
        </div>

        {/* Actions */}
        {accessStatus !== 'suspended' && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/upgrade')}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-base font-bold text-white transition-all"
              style={{ backgroundColor: 'var(--accent-primary)' }}
            >
              <Zap className="w-5 h-5" />
              Renew Subscription
            </button>
            <button
              onClick={logout}
              className="w-full py-2.5 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
              }}
            >
              Sign out
            </button>
          </div>
        )}

        {accessStatus === 'suspended' && (
          <button
            onClick={logout}
            className="w-full py-3 rounded-xl text-sm font-medium"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
            }}
          >
            Sign out
          </button>
        )}

        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Need help? Contact{' '}
          <a href="mailto:support@amortrade.com" className="underline" style={{ color: 'var(--accent-primary)' }}>
            support@amortrade.com
          </a>
        </p>
      </div>
    </div>
  );
}